import { Link, useNavigate } from 'react-router-dom';
import { colors } from '@yorukiri/ui/tokens';
import type { ApiPageSummary } from '../lib/queries';
import { Play } from './icons';

const formatRelative = (iso: string) => {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

type Counts = { due: number; learning: number; new: number };

export function PageTile({ page, counts }: { page: ApiPageSummary; counts?: Counts }) {
  const ready = counts ? counts.due + counts.learning + counts.new : 0;
  const navigate = useNavigate();
  return (
    <Link
      to={`/pages/${page.id}`}
      style={{
        background: colors.surface2,
        border: `1px solid ${colors.line2}`,
        borderRadius: 10,
        padding: '18px 20px 16px',
        textDecoration: 'none',
        color: colors.ink,
        display: 'block',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.15s, border-color 0.15s, box-shadow 0.15s',
        minHeight: 140,
      }}
    >
      <div
        style={{
          fontSize: 9.5,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: colors.ink3,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 10,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 2,
            background: page.color ?? colors.line2,
            display: 'inline-block',
          }}
        />
        Page
      </div>
      <h4
        style={{
          fontWeight: 600,
          letterSpacing: '-0.01em',
          fontSize: 22,
          lineHeight: 1.15,
          margin: 0,
          marginBottom: 6,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {page.title || 'Untitled'}
      </h4>
      {page.subtitle && (
        <div
          style={{
            fontSize: 12.5,
            color: colors.ink3,
            marginBottom: 16,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {page.subtitle}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          bottom: 12,
          left: 20,
          right: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          fontSize: 10.5,
          color: colors.ink3,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '0.06em',
        }}
      >
        <span>updated {formatRelative(page.updatedAt)}</span>
        {counts && ready > 0 && (
          <span
            role="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate(`/review/${page.id}`);
            }}
            style={reviewBadge}
          >
            <Play size={9} />
            {ready}
          </span>
        )}
      </div>
    </Link>
  );
}

const reviewBadge: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '3px 8px',
  background: colors.accent,
  color: colors.surface,
  borderRadius: 999,
  fontWeight: 500,
  letterSpacing: '0.02em',
  cursor: 'pointer',
};
